// ============================================================================
// Anaglyphic, Amsterdam 2023, All Rights Reserved.
// See README in the root project for more information.
// ============================================================================

import { defineStore } from "pinia";

// ============================================================================

interface BaseDialog {
	title: string;
	message: string;
}

interface ConfirmDialog extends BaseDialog {
	type: "confirm";
	resolve: (value: boolean) => void;
	confirmButtonText?: string;
	cancelButtonText?: string;
	isDelete?: boolean;
}

interface AlertDialog extends BaseDialog {
	type: "alert";
	resolve: () => void;
}

// Union type for different dialog types
type Dialog = ConfirmDialog | AlertDialog;
// Omit internal properties based on dialog type
export type ConfirmProps = Omit<ConfirmDialog, "resolve" | "type">;
export type AlertProps = Omit<AlertDialog, "resolve" | "type">;

// ============================================================================

/**
 * Custom dialog store instance, with a similar API to the browser's
 * `confirm` and `alert`
 */
export default defineStore("dialog", {
	state: () => ({
		current: null as Dialog | null,
	}),
	actions: {
		/**
		 * Create a confirmation dialog instance. User will be prompted with a yes or no.
		 * @param props Props to customize the dialog.
		 * @returns Promise that resolves to true if confirmed, false if cancelled.
		 */
		confirm(props?: ConfirmProps) {
			return new Promise<boolean>((resolve) => {
				props = props ?? {
					title: "Are you sure?",
					message: "Are you sure you want to continue with this action?",
				};

				this.current = {
					confirmButtonText: props.isDelete ? "Delete" : "Confirm",
					cancelButtonText: "Cancel",
					...props,
					type: "confirm",
					resolve,
				};
			});
		},
		/**
		 * Create an alert dialog instance. User will be shown an alert message.
		 * @param props Props to customize the alert.
		 * @returns Promise that resolves when the alert is acknowledged.
		 */
		alert(props?: AlertProps) {
			return new Promise<void>((resolve) => {
				props = props ?? {
					title: "Attention",
					message: "You cannot do that!",
				};

				this.current = { ...props, type: "alert", resolve };
			});
		},
	},
});
